<template>
    <v-dialog v-model="dialog" persistent scrollable max-width="600px" hide-overlay>
        <template v-slot:activator="{ on }">
            <v-btn color="secondary" class="text-lowercase" icon v-on="on">
                <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on }">
                        <v-icon small v-on="on">mdi-lead-pencil</v-icon>
                    </template>
                    <span>{{'add' | localize }}/{{'modify resources' | localize}}</span>
                </v-tooltip>
            </v-btn>
        </template>
        <v-card v-if="isLoaded">
            <v-card-title class="subtitle-1 pt-6">
                {{'Modify resources for the next round' | localize}}
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="black--text">
                <v-container grid-list-md>
                    <v-form v-model="valid" ref="form" validation>
                        <v-row>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field dense color="secondary" 
                                    :label="'Capacity current round' | localize"
                                    :value="formatNumber(capacities)"
                                    suffix="units*" 
                                    name="capacities" type="text"
                                    readonly>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field dense color="secondary" 
                                    :label="'Capacity increase for the next round' | localize"
                                    v-model.number="capacities_increase"
                                    :hint="`not more than ${formatNumber(this.capHint)}`"
                                    suffix="units" 
                                    name="capacities_increase" type="text"
                                    :rules="[rules.cap_hint]" 
                                    @change="onceMore()">
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field dense color="secondary" 
                                    :label="'Long-term debt' | localize"
                                    prefix="$"
                                    suffix="*" 
                                    :value="formatNumber(long_term_debt)" 
                                    name="debt" type="text"
                                    readonly>
                                </v-text-field>
                            </v-col> 
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field dense color="secondary" 
                                    :label="'Borrowings' | localize"
                                    prefix="$"
                                    :hint="`not more than $${formatNumber(this.borrowHint)}`"
                                    v-model.number="borrowings" 
                                    name="borrowings" type="text"
                                    :rules="[rules.borrow_hint]" 
                                    @change="onceMore()">
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="6">
                                <v-select dense multiple
                                    return-object
                                    v-model="selected_segments" 
                                    :items="market_segments"
                                    item-text="segment.name" 
                                    item-value="max_price" 
                                    color="secondary" 
                                    item-color="secondary"
                                    :hint="'maximum 2 segments' | localize"
                                    persistent-hint
                                    :label="'Segments' | localize"
                                    :readonly="readable"
                                    @change="onceMore()"
                                    :rules="[rules.max]">
                                </v-select>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-container>
                <v-row class="ma-4 caption font-weight-bold">* {{'you cannot modify this parameter' | localize}}</v-row> 
                <v-row v-if="error" class="text-center error--text">{{'Error' | localize }} => {{ error }}</v-row>
                <v-row class="text-start text-pre-wrap">
                    {{ message }}
                </v-row>    
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="secondary" text class="text-capitalize" 
                    :disabled="!valid || Boolean(error)"
                    @click="calculating">
                    {{'Calculate' | localize}}
                </v-btn>

                <v-btn color="secondary" text class="text-capitalize"
                    :disabled="!calculate || Boolean(error)"
                    @click="editResource">{{'Save' | localize}}
                </v-btn>

                <v-btn color="secondary" text class="text-capitalize" @click="closeDialog">
                    {{'Close' | localize}}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import localizeFilter from '../../../filters/localize.filter.js';
import { localeNumber, roundNumber} from '../../../functions/common.js';
import { ResearchCosts } from '../../../functions/resources.js';

export default {
    name: 'EditResource',
    props: ['resource'],
    data() {
        return {
            dialog: false,
            valid: false,
            error: '',
            message: '',
            calculate: false,
            isLoaded: false,
            rules: {
                required: v => !!v || localizeFilter('required field'),
                number: v => /^[+-]?[0-9]/.test(v) || localizeFilter('its expected number'),
                max: v => v.length <= 2 || localizeFilter('max 2 expected'),
                cap_hint: v => {
                    if (!isNaN(parseFloat(v)) && v <= this.capHint) return true;
                    return localizeFilter('its expected number not more than') + `${this.capHint}`;
                },
                borrow_hint: v => {
                    if (!isNaN(parseFloat(v)) && v <= this.borrowHint) return true;
                    return localizeFilter('its expected number not more than') + `${this.borrowHint}`;
                }
            },
            //props
            /* selected , */
            segments: [],
            capacities: this.resource.capacities,
            long_term_debt: this.resource.long_term_debt,
            capacities_increase: this.resource.capacities_increase,
            borrowings: this.resource.borrowings,
            /*  calculating params */
            //market_research: 0, 
            research_costs: this.resource.research_costs,
        }
    },
    computed: {
        locale() {
            return this.$store.getters.locale;
        },
        game() {
            return this.$store.getters.game;
        },
        market_segments() {
            return this.$store.getters.market_segments;
        },
        finances() {
            return this.$store.getters.finances;
        },
        borrowHint() {
            let hint = 0;
            if (this.finances) {
                hint = roundNumber(this.finances.growth_finances * 0.7);
            }
            return hint;
        },
        capHint() {
            let hint = 0;
            if (this.finances) {
                const growth_finances = this.finances?.growth_finances + this.borrowings;
                hint = roundNumber(growth_finances / 10);
            }
            return hint;
        },
        selected_segments: {
            get() {
                let segments = [];
                if (this.resource) {
                    this.resource?.research_segments?.forEach((item) => {
                        const segment = this.market_segments?.find((seg) => seg.segment.name == item)
                        if (segment) {
                            segments.push(segment);
                        }
                    })
               }
               return segments;
            },
            set(val) {
                this.segments = val;
            }
        },
        readable() {
            if (this.resource?.research_costs > 0 || this.research_segments?.length > 0) return true
            return false
        }
    },
    methods: {
        formatNumber(num) {
            return localeNumber(num, this.locale);
        },
        onceMore() {
            this.error = ''
            this.calculate = false;
            this.message = '';
        },
        calculating() {
            if (this.$refs.form.validate()) {
                
                this.research_costs = ResearchCosts(this.segments);
                
                if (this.capacities_increase == this.resource.capacities_increase &&
                    this.borrowings == this.resource.borrowings &&
                    this.research_costs >= 0 && this.segments?.length == 0
                ) {
                    return this.error = localizeFilter('no parameters have been changed');
                }

                const investments = this.capacities_increase * 100;

                /* console.log(this.locale);*/
				if (this.locale == 'ro-RO') {
					this.message = `Vă rugăm să luați notă:

					Investiții = $${this.formatNumber(investments)}
					Costurile de cercetare = $${this.formatNumber(this.research_costs)} 
					--------------------------------------------------------------
					Dacă sunteți de acord, apăsați butonul "Salvare", altfel - apăsați "Închide"`
				} 
				else if (this.locale == 'ru-RU') {
					this.message = `Пожалуйста, обратите внимание:

					Инвестиции = $${this.formatNumber(investments)}
					Расходы на исследования = $${this.formatNumber(this.research_costs)} 
					-------------------------------------------------------
				    Согласны? Нажмите кнопку "Сохранить", иначе — кнопку "Закрыть"`
				}
				else {
					this.message = `Please take note:

					Investments = $${this.formatNumber(investments)}
					Research costs = $${this.formatNumber(this.research_costs)} 
					---------------------------------------------------------
					If you agree, press the button "Save", else - press "Close"`
				}

                this.calculate = true;
            }
        },
        async editResource() {

            if (this.$refs.form.validate()) {                
                const resource = {
                    id: this.resource.id
                }

                if (this.capacities_increase !== this.resource.capacities_increase) {
                    resource.capacities_increase = this.capacities_increase;
                }

                if (this.borrowings !== this.resource.borrowings) {
                    resource.borrowings = this.borrowings;
                }
                
                const segments = this.segments.map(seg => seg.segment.name);

                if (this.resource.research_costs == 0 && this.research_costs > 0) {
                    resource.research_segments = segments;
                    resource.research_costs = this.research_costs;
                } 
                /*console.log(resource);*/
                try {
                    //update resource
                    await this.$store.dispatch('updateTeamResource', resource)
                    this.closeDialog()
                }
                catch (error) {
                    this.error = error.message;
                    this.message = '';
                    this.$store.dispatch('setError', error.message)//throw error
                }
            } 
        },
        closeDialog() {
            //reset
            this.dialog = false;
            this.error = '';
            this.message = '';
            this.calculate = false;

            this.capacities = this.resource.capacities; 
            this.capacities_increase = this.resource.capacities_increase;
            this.borrowings = this.resource.borrowings;
            this.long_term_debt = this.resource.long_term_debt;
            this.research_costs = this.resource.research_costs;

            this.selected_segments = () => {
                const res_arr = this.resource.research_segments?.map(seg => {
                    const market_segment = this.market_segments?.find(item => item.segment.name == seg)
                    let max_price = 0
                    if (market_segment) {
                        max_price = market_segment.max_price
                    }
                    return { name: seg, max_price: max_price };
                })
                return res_arr
            }
        }
    },
    async created() {

		if (this.game) {

			if (!this.market_segments?.length) {
				try {
					await this.$store.dispatch('fetchMarketSegments', { gameId: this.game.id, round: this.game.round });
				}
				catch (error) {
					await this.$store.dispatch('setError', error.message)
				}
			}
		}

		this.isLoaded = true;
    }
}
</script>